import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { gql } from '../../graphql/client';
import {
  M_INSERT_VIEW,
  M_INSERT_VIEW_VIEW,
  Q_LIST_NAV_VIEWS,
} from '../../graphql/operations';
import type { ChildViewEdge, View } from '../../types/forge';
import {
  applyTemplate,
  replaceRoot,
  TEMPLATES,
  type Template,
} from './navigationTemplates';

/**
 * App-level navigation-structure editor.
 *
 * Two states driven by `FrontendApp.main_view_id`:
 *
 *   - Empty: render quick-start template cards. Picking one seeds a tiny
 *     view hierarchy and points main_view_id at its root.
 *
 *   - Configured: render the navigation skeleton (only routable types —
 *     Screen / StackNavigator / TabNavigator) starting at main_view_id.
 *     Each navigator gets inline `+ Add Screen` / `+ Add Stack` actions,
 *     each row gets `Open in Builder →` (deep-links with ?selectView=<id>).
 *     A footer `Replace root…` button performs a destructive
 *     delete-and-reseed.
 *
 * The section consumes a *lean* views query (Q_LIST_NAV_VIEWS) — no
 * viewStyle / viewSource / interactions — to keep the App Settings page
 * fast. The View Builder still owns the heavyweight query for editing.
 */
export interface NavigationStructureSectionProps {
  appId: string;
  /** From the parent's Q_GET_FRONTEND_APP query. */
  mainViewId: string | null;
  /**
   * Bumped by the parent when settings save invalidates queries — also
   * triggers a refetch of the lean views query.
   */
  appVersionTag?: string | number;
}

const ROUTE_TYPES: ReadonlySet<string> = new Set([
  'Screen',
  'StackNavigator',
  'TabNavigator',
]);

const NAVIGATOR_TYPES: ReadonlySet<string> = new Set([
  'StackNavigator',
  'TabNavigator',
]);

interface NavViewsData {
  View: Array<{
    id: string;
    Type: string;
    name: string;
    sort: number;
    child_views: ChildViewEdge[];
  }>;
}

export function NavigationStructureSection({
  appId,
  mainViewId,
}: NavigationStructureSectionProps) {
  const queryClient = useQueryClient();

  const viewsQ = useQuery({
    queryKey: ['nav-views', appId],
    enabled: !!appId,
    queryFn: () => gql<NavViewsData>(Q_LIST_NAV_VIEWS, { appId }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['nav-views', appId] });
    queryClient.invalidateQueries({ queryKey: ['frontend-app', appId] });
    queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
  };

  const apply = useMutation({
    mutationFn: (t: Template) => applyTemplate(appId, t),
    onSuccess: invalidate,
  });

  const replace = useMutation({
    mutationFn: ({ oldRootId, template }: { oldRootId: string; template: Template }) =>
      replaceRoot(appId, oldRootId, template),
    onSuccess: invalidate,
  });

  const addChild = useMutation({
    mutationFn: async ({
      parentId,
      type,
      seedHomeScreen,
    }: {
      parentId: string;
      type: 'Screen' | 'StackNavigator' | 'TabNavigator';
      seedHomeScreen?: boolean;
    }) => {
      // Pick the next sort under the target navigator.
      const parent = viewsById.get(parentId);
      const edges = parent?.child_views ?? [];
      const nextSort =
        edges.length === 0 ? 0 : Math.max(...edges.map((e) => e.sort)) + 1;
      const baseName = nextChildName(type, edges, viewsById);
      const created = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type,
        name: baseName,
        sort: nextSort,
      });
      const newId = created.insert_View_one.id;
      await gql(M_INSERT_VIEW_VIEW, { parentId, childId: newId, sort: nextSort });
      // Newly-added Stack/Tab gets one initial Screen so it isn't empty.
      if (seedHomeScreen) {
        const home = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
          appId,
          type: 'Screen',
          name: `${baseName} Home`,
          sort: 0,
        });
        await gql(M_INSERT_VIEW_VIEW, {
          parentId: newId,
          childId: home.insert_View_one.id,
          sort: 0,
        });
      }
      return newId;
    },
    onSuccess: invalidate,
  });

  const viewsById = new Map<string, NavViewsData['View'][number]>(
    (viewsQ.data?.View ?? []).map((v) => [v.id, v]),
  );

  /* -------------------------- Render branches -------------------------- */

  if (viewsQ.isLoading) {
    return <Section><p style={s.hint}>Loading navigation…</p></Section>;
  }
  if (viewsQ.error) {
    return (
      <Section>
        <p style={s.errorText}>
          Failed to load navigation: {(viewsQ.error as Error).message}
        </p>
      </Section>
    );
  }

  if (!mainViewId) {
    return (
      <Section>
        <h2 style={s.cardTitle}>Navigation Structure</h2>
        <p style={s.cardHint}>
          Pick a starting point. You can replace this later.
        </p>
        <div style={s.templateGrid}>
          {TEMPLATES.map((t) => (
            <TemplateCard
              key={t.id}
              meta={t}
              busy={apply.isPending}
              onPick={() => apply.mutate(t.id)}
            />
          ))}
        </div>
        {apply.isError && (
          <p style={s.errorText}>
            {(apply.error as Error).message}
          </p>
        )}
        <p style={s.subtleLink}>
          or open the{' '}
          <Link to={`/apps/${appId}/builder`} style={s.inlineLink}>View Builder</Link>{' '}
          and drag a navigator from the palette.
        </p>
      </Section>
    );
  }

  const root = viewsById.get(mainViewId);
  if (!root) {
    return (
      <Section>
        <h2 style={s.cardTitle}>Navigation Structure</h2>
        <p style={s.errorText}>
          Root view <code style={s.code}>{mainViewId}</code> not found.
          Try refreshing.
        </p>
      </Section>
    );
  }

  return (
    <Section>
      <div style={s.cardHeader}>
        <h2 style={s.cardTitle}>Navigation Structure</h2>
        <Link
          to={`/apps/${appId}/builder?selectView=${mainViewId}`}
          style={s.openBuilderInline}
        >
          Open in Builder →
        </Link>
      </div>

      <p style={s.cardHint}>
        The navigation skeleton — only Screen, Stack, and Tab nodes are
        shown. Use the View Builder to author each screen's content.
      </p>

      <div style={s.tree}>
        <NavRow
          appId={appId}
          view={root}
          viewsById={viewsById}
          depth={0}
          onAddChild={(parentId, type, seedHomeScreen) =>
            addChild.mutate({ parentId, type, seedHomeScreen })
          }
          isAdding={addChild.isPending}
        />
      </div>

      {addChild.isError && (
        <p style={s.errorText}>{(addChild.error as Error).message}</p>
      )}

      <ReplaceRootButton
        oldRootId={root.id}
        busy={replace.isPending}
        onReplace={(template) => replace.mutate({ oldRootId: root.id, template })}
      />
      {replace.isError && (
        <p style={s.errorText}>{(replace.error as Error).message}</p>
      )}
    </Section>
  );
}

/* ============================== Sub-views =============================== */

function Section({ children }: { children: React.ReactNode }) {
  return <section style={s.card}>{children}</section>;
}

function TemplateCard({
  meta,
  busy,
  onPick,
}: {
  meta: { id: Template; title: string; description: string };
  busy: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy}
      style={{ ...s.templateCard, ...(busy ? s.templateCardDisabled : {}) }}
    >
      <TemplateIcon id={meta.id} />
      <span style={s.templateTitle}>{meta.title}</span>
      <span style={s.templateDesc}>{meta.description}</span>
      <span style={s.templateCta}>{busy ? 'Working…' : 'Use this template'}</span>
    </button>
  );
}

/** Tiny purely-visual SVG mock per template — keeps the picker scannable. */
function TemplateIcon({ id }: { id: Template }) {
  if (id === 'single-screen') {
    return (
      <svg viewBox="0 0 60 60" style={s.templateIcon} aria-hidden>
        <rect x="14" y="6" width="32" height="48" rx="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <line x1="20" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="1.5" />
        <line x1="20" y1="28" x2="36" y2="28" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
        <line x1="20" y1="34" x2="32" y2="34" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
      </svg>
    );
  }
  if (id === 'stack') {
    return (
      <svg viewBox="0 0 60 60" style={s.templateIcon} aria-hidden>
        <rect x="22" y="2" width="32" height="48" rx="4" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
        <rect x="16" y="6" width="32" height="48" rx="4" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.65" />
        <rect x="10" y="10" width="32" height="48" rx="4" fill="white" stroke="currentColor" strokeWidth="1.5" />
        <path d="M16 16 l-3 3 3 3" stroke="currentColor" strokeWidth="1.2" fill="none" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 60 60" style={s.templateIcon} aria-hidden>
      <rect x="6" y="6" width="48" height="48" rx="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <line x1="6" y1="44" x2="54" y2="44" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16" cy="50" r="2" fill="currentColor" />
      <circle cx="30" cy="50" r="2" fill="currentColor" />
      <circle cx="44" cy="50" r="2" fill="currentColor" />
    </svg>
  );
}

interface NavRowProps {
  appId: string;
  view: { id: string; Type: string; name: string; child_views: ChildViewEdge[] };
  viewsById: Map<string, { id: string; Type: string; name: string; child_views: ChildViewEdge[] }>;
  depth: number;
  /**
   * Called when the author clicks `+ Screen` / `+ Stack` on this row's
   * navigator. The child gets inserted under `parentId` (always this
   * row's `view.id`).
   */
  onAddChild: (
    parentId: string,
    type: 'Screen' | 'StackNavigator' | 'TabNavigator',
    seedHomeScreen?: boolean,
  ) => void;
  isAdding: boolean;
}

function NavRow({
  appId,
  view,
  viewsById,
  depth,
  onAddChild,
  isAdding,
}: NavRowProps) {
  const isNav = NAVIGATOR_TYPES.has(view.Type);
  const sortedEdges = [...(view.child_views ?? [])].sort((a, b) => a.sort - b.sort);
  const routableChildren = sortedEdges
    .map((e) => viewsById.get(e.related_view_id))
    .filter((v): v is NavRowProps['view'] => !!v && ROUTE_TYPES.has(v.Type));

  return (
    <div style={{ ...s.navRow, marginLeft: depth * 18 }}>
      <div style={s.navRowHead}>
        <TypeBadge type={view.Type} />
        <span style={s.navName}>{view.name}</span>
        <Link
          to={`/apps/${appId}/builder?selectView=${view.id}`}
          style={s.openLink}
          title="Edit this view in the Builder"
        >
          Edit →
        </Link>
      </div>

      {routableChildren.length > 0 && (
        <div style={s.children}>
          {routableChildren.map((child) => (
            <NavRow
              key={child.id}
              appId={appId}
              view={child}
              viewsById={viewsById}
              depth={depth + 1}
              onAddChild={onAddChild}
              isAdding={isAdding}
            />
          ))}
        </div>
      )}

      {/*
        Add buttons live on every navigator (root or nested). Leaf Screen
        rows have no add buttons — they're terminal.
      */}
      {isNav && (
        <NavAddButtons
          busy={isAdding}
          onAdd={(type, seed) => onAddChild(view.id, type, seed)}
        />
      )}
    </div>
  );
}

function NavAddButtons({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (
    type: 'Screen' | 'StackNavigator' | 'TabNavigator',
    seedHomeScreen?: boolean,
  ) => void;
}) {
  // Nested navigators can hold child screens / nested stacks. We always
  // expose Screen + Stack on every navigator. We DON'T expose nested
  // TabNavigator from inside another nav — nested tab bars are a sharp
  // edge in React Navigation, and the author can still drag one in from
  // the builder palette if they really want it.
  return (
    <div style={s.addRow}>
      <button
        type="button"
        disabled={busy}
        onClick={() => onAdd('Screen')}
        style={s.addBtnPrimary}
      >
        + Screen
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onAdd('StackNavigator', true)}
        style={s.addBtnSecondary}
      >
        + Stack
      </button>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const variant = NAVIGATOR_TYPES.has(type) ? s.badgeNav : s.badgeScreen;
  const label =
    type === 'StackNavigator' ? 'STACK' :
    type === 'TabNavigator'   ? 'TABS'  :
    type === 'Screen'         ? 'SCREEN' :
    type.toUpperCase();
  return <span style={{ ...s.badge, ...variant }}>{label}</span>;
}

function ReplaceRootButton({
  oldRootId,
  busy,
  onReplace,
}: {
  oldRootId: string;
  busy: boolean;
  onReplace: (template: Template) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div style={s.replaceRow}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={busy}
          style={s.replaceBtn}
        >
          {busy ? 'Replacing…' : 'Replace root…'}
        </button>
        <span style={s.replaceHint}>
          Destructive — deletes every view under the current root.
        </span>
      </div>

      {open && (
        <div style={s.modalBackdrop} onClick={() => setOpen(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Replace root?</h3>
            <p style={s.modalBody}>
              This deletes the current root and everything under it. Every
              screen, style, source, and interaction in the existing
              hierarchy will be removed. There is no undo.
            </p>
            <p style={s.modalBody}>Pick the new root structure:</p>
            <div style={s.modalCards}>
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onReplace(t.id);
                  }}
                  style={s.modalCard}
                >
                  <TemplateIcon id={t.id} />
                  <span style={s.templateTitle}>{t.title}</span>
                </button>
              ))}
            </div>
            <div style={s.modalActions}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={s.modalCancel}
              >
                Cancel
              </button>
            </div>
            <p style={s.modalDeleteId}>
              Will delete: <code style={s.code}>{oldRootId}</code>
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------- Helpers ------------------------------- */

function nextChildName(
  type: string,
  siblings: ChildViewEdge[],
  viewsById: Map<string, { name: string }>,
): string {
  const usedNames = new Set(
    siblings
      .map((e) => viewsById.get(e.related_view_id)?.name)
      .filter((n): n is string => !!n),
  );
  const base =
    type === 'Screen'         ? 'Screen' :
    type === 'StackNavigator' ? 'Stack'  :
    type === 'TabNavigator'   ? 'Tabs'   :
    'View';
  for (let i = 1; i < 1000; i++) {
    const candidate = `${base} ${i}`;
    if (!usedNames.has(candidate)) return candidate;
  }
  return `${base} ${Date.now() % 10000}`;
}

/* ------------------------------- Styles -------------------------------- */

const s: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 4,
  },
  cardTitle: {
    margin: '0 0 4px',
    fontSize: 14,
    fontWeight: 700,
    color: '#0F172A',
  },
  cardHint: { margin: '0 0 16px', fontSize: 12, color: '#94A3B8' },
  hint: { margin: 0, fontSize: 12, color: '#94A3B8' },
  errorText: { margin: '8px 0 0', fontSize: 12, color: '#c00' },
  inlineLink: { color: '#4F46E5', textDecoration: 'underline' },
  subtleLink: { margin: '12px 0 0', fontSize: 11, color: '#94A3B8' },
  code: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 11,
    backgroundColor: '#F1F5F9',
    padding: '1px 5px',
    borderRadius: 3,
  },

  /* Empty state — template grid */
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  templateCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    padding: 14,
    textAlign: 'left' as const,
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    cursor: 'pointer',
    color: '#0F172A',
    transition: 'border-color 80ms, box-shadow 80ms',
  },
  templateCardDisabled: { opacity: 0.6, cursor: 'wait' },
  templateIcon: { width: 40, height: 40, color: '#4F46E5' },
  templateTitle: { fontSize: 13, fontWeight: 700, color: '#0F172A' },
  templateDesc: { fontSize: 11, color: '#64748B', lineHeight: 1.4 },
  templateCta: {
    marginTop: 6,
    fontSize: 11,
    fontWeight: 600,
    color: '#4F46E5',
  },

  /* Configured state */
  openBuilderInline: {
    fontSize: 12,
    fontWeight: 600,
    color: '#4F46E5',
    textDecoration: 'none',
    flexShrink: 0,
  },
  tree: {
    border: '1px solid #E2E8F0',
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
    backgroundColor: '#FAFBFC',
  },
  navRow: { padding: '4px 0' },
  navRowHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 28,
  },
  navName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#0F172A',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  openLink: {
    fontSize: 11,
    fontWeight: 600,
    color: '#4F46E5',
    textDecoration: 'none',
    padding: '2px 8px',
    border: '1px solid #C7D2FE',
    borderRadius: 4,
  },
  children: { borderLeft: '1px dashed #CBD5E1', paddingLeft: 8, marginTop: 4 },

  badge: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.5,
    padding: '2px 6px',
    borderRadius: 3,
    flexShrink: 0,
  },
  badgeNav: { backgroundColor: '#EEF2FF', color: '#4F46E5' },
  badgeScreen: { backgroundColor: '#ECFDF5', color: '#047857' },

  addRow: { display: 'flex', gap: 6, marginTop: 6 },
  addBtnPrimary: {
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    padding: '4px 10px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
  },
  addBtnSecondary: {
    fontSize: 11,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    padding: '4px 10px',
    border: '1px solid #C7D2FE',
    borderRadius: 4,
    cursor: 'pointer',
  },

  /* Replace root */
  replaceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingTop: 10,
    borderTop: '1px solid #F1F5F9',
  },
  replaceBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: '#DC2626',
    backgroundColor: '#fff',
    border: '1px solid #FECACA',
    borderRadius: 4,
    padding: '6px 12px',
    cursor: 'pointer',
  },
  replaceHint: { fontSize: 11, color: '#94A3B8' },

  /* Replace modal */
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  modal: {
    width: 520,
    maxWidth: '90vw',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 22,
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.25)',
  },
  modalTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#0F172A' },
  modalBody: { margin: '0 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.5 },
  modalCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
    marginTop: 12,
    marginBottom: 14,
  },
  modalCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: 12,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#0F172A',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end' },
  modalCancel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px 12px',
  },
  modalDeleteId: { margin: '8px 0 0', fontSize: 11, color: '#94A3B8' },
};
